import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TableRow,
  TableCell,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import { Search, Info } from '@mui/icons-material';
import {
  Add,
  Edit,
  DeleteOutline,
  Sensors,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { CorporateTitle } from '../components/ui/CorporateTitle';
import { CorporateButton } from '../components/ui/CorporateButton';
import { CorporateTable } from '../components/ui/CorporateTable';
import { CorporateDialog } from '../components/ui/CorporateDialog';
import { CorporateTextField } from '../components/ui/CorporateTextField';
import { ActionButtonGroup } from '../components/ui/ActionButtonGroup';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { FOSSensor, FOSTipologia } from '../types';
import { useAuth } from '../contexts/AuthContext';

type FOSSensorFormState = {
  serial_number: string;
  customer_code: string;
  tipologia: FOSTipologia;
  product_type: string;
  product_description: string;
  gauge_length: string;
  wavelength_range: string;
  lead_in_length: string;
  lead_out_length: string;
  connector_type: string;
  cable_config: string;
  additional_config: string;
  strain_coefficient_a: string;
  strain_coefficient_b: string;
  free_fiber_length: string;
  cte: string;
  temp_sensitivity_1: string;
  temp_sensitivity_2: string;
  temp_sensitivity_3: string;
  temp_ref_wavelength: string;
};

type FormErrors = Partial<Record<keyof FOSSensorFormState, string>>;

const emptyFormState: FOSSensorFormState = {
  serial_number: '',
  customer_code: '',
  tipologia: 'Long-Gauge',
  product_type: '',
  product_description: '',
  gauge_length: '',
  wavelength_range: '',
  lead_in_length: '',
  lead_out_length: '',
  connector_type: '',
  cable_config: '',
  additional_config: '',
  strain_coefficient_a: '',
  strain_coefficient_b: '',
  free_fiber_length: '',
  cte: '',
  temp_sensitivity_1: '',
  temp_sensitivity_2: '',
  temp_sensitivity_3: '',
  temp_ref_wavelength: '',
};

export const FOSSensorsPage: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_FOS_SENSORS');
  const theme = useTheme();

  const [sensors, setSensors] = useState<FOSSensor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<FOSSensorFormState>(emptyFormState);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<FOSSensor | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tipologiaFilter, setTipologiaFilter] = useState<string>('');
  const [productTypeFilter, setProductTypeFilter] = useState<string>('');
  const [showFormulasInfo, setShowFormulasInfo] = useState(false);

  const loadSensors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: any = {
        skip: 0,
        limit: 1000,
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (tipologiaFilter) {
        params.tipologia = tipologiaFilter;
      }
      if (productTypeFilter) {
        params.product_type = productTypeFilter;
      }
      const data = await apiService.getFOSSensors(params);
      setSensors(data.sensors);
    } catch (err) {
      setError('No se pudieron cargar los sensores FOS');
      showError('No se pudieron cargar los sensores FOS');
    } finally {
      setIsLoading(false);
    }
  }, [showError, searchQuery, tipologiaFilter, productTypeFilter]);

  useEffect(() => {
    loadSensors();
  }, [loadSensors]);

  const filteredSensors = useMemo(() => {
    return sensors;
  }, [sensors]);

  const openCreateDialog = () => {
    if (!canManage) return;
    setSelectedSensor(null);
    setFormState(emptyFormState);
    setFormErrors({});
    setIsDialogOpen(true);
  };

  // Determinar tipología basada en product_type
  // SC-01/T: Long-Gauge (usa fórmula completa con compensación de temperatura)
  // MS-03, SGCalsens: Short-Gauge (usa fórmula simplificada sin compensación de temperatura)
  const getTipologiaFromProductType = (productType: string): FOSTipologia => {
    if (!productType) return 'Long-Gauge';
    const pt = productType.toUpperCase();
    if (pt.includes('SC-01/T')) {
      return 'Long-Gauge';
    } else if (pt.includes('MS-03') || pt.includes('SGCALSENS')) {
      return 'Short-Gauge';
    }
    return 'Long-Gauge'; // Por defecto
  };

  const openEditDialog = (sensor: FOSSensor) => {
    if (!canManage) return;
    setSelectedSensor(sensor);
    const tipologia = getTipologiaFromProductType(sensor.product_type);
    setFormState({
      serial_number: sensor.serial_number,
      customer_code: sensor.customer_code || '',
      tipologia: tipologia,
      product_type: sensor.product_type,
      product_description: sensor.product_description || '',
      gauge_length: sensor.gauge_length || '',
      wavelength_range: sensor.wavelength_range || '',
      lead_in_length: sensor.lead_in_length || '',
      lead_out_length: sensor.lead_out_length || '',
      connector_type: sensor.connector_type || '',
      cable_config: sensor.cable_config || '',
      additional_config: sensor.additional_config || '',
      strain_coefficient_a: sensor.strain_coefficient_a.toString(),
      strain_coefficient_b: sensor.strain_coefficient_b.toString(),
      free_fiber_length: sensor.free_fiber_length.toString(),
      cte: sensor.cte.toString(),
      temp_sensitivity_1: sensor.temp_sensitivity_1?.toString() || '',
      temp_sensitivity_2: sensor.temp_sensitivity_2?.toString() || '',
      temp_sensitivity_3: sensor.temp_sensitivity_3?.toString() || '',
      temp_ref_wavelength: sensor.temp_ref_wavelength?.toString() || '',
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const closeDialogs = () => {
    setIsDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setSelectedSensor(null);
    setFormErrors({});
    setIsSubmitting(false);
  };

  const requestDelete = (sensor: FOSSensor) => {
    if (!canManage) return;
    setSelectedSensor(sensor);
    setIsDeleteDialogOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    if (!formState.serial_number.trim()) {
      errors.serial_number = 'El número de serie es obligatorio';
    }
    if (!formState.product_type.trim()) {
      errors.product_type = 'El tipo de producto es obligatorio';
    }
    if (!formState.strain_coefficient_a.trim()) {
      errors.strain_coefficient_a = 'El coeficiente A es obligatorio';
    } else if (isNaN(Number(formState.strain_coefficient_a))) {
      errors.strain_coefficient_a = 'El coeficiente A debe ser un número válido';
    }
    if (!formState.strain_coefficient_b.trim()) {
      errors.strain_coefficient_b = 'El coeficiente B es obligatorio';
    } else if (isNaN(Number(formState.strain_coefficient_b))) {
      errors.strain_coefficient_b = 'El coeficiente B debe ser un número válido';
    }
    if (!formState.free_fiber_length.trim()) {
      errors.free_fiber_length = 'La longitud de fibra libre es obligatoria';
    } else if (isNaN(Number(formState.free_fiber_length)) || Number(formState.free_fiber_length) <= 0) {
      errors.free_fiber_length = 'La longitud de fibra libre debe ser un número mayor que 0';
    }
    if (!formState.cte.trim()) {
      errors.cte = 'El CTE es obligatorio';
    } else if (isNaN(Number(formState.cte))) {
      errors.cte = 'El CTE debe ser un número válido';
    }
    
    // Validar campos de temperatura si están presentes
    if (formState.temp_sensitivity_1 && isNaN(Number(formState.temp_sensitivity_1))) {
      errors.temp_sensitivity_1 = 'Debe ser un número válido';
    }
    if (formState.temp_sensitivity_2 && isNaN(Number(formState.temp_sensitivity_2))) {
      errors.temp_sensitivity_2 = 'Debe ser un número válido';
    }
    if (formState.temp_sensitivity_3 && isNaN(Number(formState.temp_sensitivity_3))) {
      errors.temp_sensitivity_3 = 'Debe ser un número válido';
    }
    if (formState.temp_ref_wavelength && isNaN(Number(formState.temp_ref_wavelength))) {
      errors.temp_ref_wavelength = 'Debe ser un número válido';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof FOSSensorFormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    if (!canManage || !validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload: any = {
        serial_number: formState.serial_number.trim(),
        customer_code: formState.customer_code.trim() || null,
        tipologia: formState.tipologia,
        product_type: formState.product_type.trim(),
        product_description: formState.product_description.trim() || null,
        gauge_length: formState.gauge_length.trim() || null,
        wavelength_range: formState.wavelength_range.trim() || null,
        lead_in_length: formState.lead_in_length.trim() || null,
        lead_out_length: formState.lead_out_length.trim() || null,
        connector_type: formState.connector_type.trim() || null,
        cable_config: formState.cable_config.trim() || null,
        additional_config: formState.additional_config.trim() || null,
        strain_coefficient_a: Number(formState.strain_coefficient_a),
        strain_coefficient_b: Number(formState.strain_coefficient_b),
        free_fiber_length: Number(formState.free_fiber_length),
        cte: Number(formState.cte),
        temp_sensitivity_1: formState.temp_sensitivity_1 ? Number(formState.temp_sensitivity_1) : null,
        temp_sensitivity_2: formState.temp_sensitivity_2 ? Number(formState.temp_sensitivity_2) : null,
        temp_sensitivity_3: formState.temp_sensitivity_3 ? Number(formState.temp_sensitivity_3) : null,
        temp_ref_wavelength: formState.temp_ref_wavelength ? Number(formState.temp_ref_wavelength) : null,
      };

      if (selectedSensor) {
        await apiService.updateFOSSensor(selectedSensor.id, payload);
        showSuccess('Sensor FOS actualizado correctamente');
      } else {
        await apiService.createFOSSensor(payload);
        showSuccess('Sensor FOS creado correctamente');
      }
      await loadSensors();
      closeDialogs();
    } catch (err: any) {
      let errorMessage = 'No se pudo guardar el sensor FOS';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      showError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canManage || !selectedSensor) return;
    setIsSubmitting(true);
    try {
      await apiService.deleteFOSSensor(selectedSensor.id);
      showSuccess('Sensor FOS eliminado correctamente');
      await loadSensors();
      closeDialogs();
    } catch (err: any) {
      let errorMessage = 'No se pudo eliminar el sensor FOS';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      showError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CorporateTitle>
              <Sensors sx={{ mr: 1, verticalAlign: 'middle' }} />
              Base de Datos FOS
            </CorporateTitle>
            <Tooltip title="Información sobre fórmulas de cálculo" arrow>
              <IconButton 
                size="small" 
                onClick={() => setShowFormulasInfo(true)}
                sx={{ ml: 1 }}
              >
                <Info fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {canManage && (
            <CorporateButton
              startIcon={<Add />}
              onClick={openCreateDialog}
              variant="contained"
            >
              Nuevo Sensor
            </CorporateButton>
          )}
        </Stack>

        {/* Filtros */}
        <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
          <TextField
            placeholder="Buscar por serial, código cliente, tipo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 250, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tipología</InputLabel>
            <Select
              value={tipologiaFilter}
              label="Tipología"
              onChange={(e) => setTipologiaFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="Long-Gauge">Long-Gauge</MenuItem>
              <MenuItem value="Short-Gauge">Short-Gauge</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tipo Producto</InputLabel>
            <Select
              value={productTypeFilter}
              label="Tipo Producto"
              onChange={(e) => setProductTypeFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="SC-01/T">SC-01/T</MenuItem>
              <MenuItem value="MS-03">MS-03</MenuItem>
              <MenuItem value="SGCalsens">SGCalsens</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Tabla */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box p={4}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : filteredSensors.length === 0 ? (
          <EmptyState
            icon={<Sensors />}
            title="No hay sensores FOS"
            description={
              canManage
                ? 'Comienza creando tu primer sensor FOS'
                : 'No hay sensores FOS disponibles'
            }
          />
        ) : (
          <CorporateTable
            headers={[
              { label: 'Serial Number', key: 'serial_number', sortable: true },
              { label: 'Tipología', key: 'tipologia', sortable: true },
              { label: 'Tipo Producto', key: 'product_type', sortable: true },
              { label: 'Descripción', key: 'product_description', sortable: false },
              { label: 'A', key: 'strain_a', sortable: false },
              { label: 'B', key: 'strain_b', sortable: false },
              { label: 'L_FFL', key: 'l_ffl', sortable: false },
              { label: 'CTE', key: 'cte', sortable: false },
              { label: 'TS1', key: 'temp_s1', sortable: false },
              { label: 'TS2', key: 'temp_s2', sortable: false },
              { label: 'TS3', key: 'temp_s3', sortable: false },
              { label: 'λ_T,ref', key: 'temp_ref', sortable: false },
              ...(canManage ? [{ label: 'Acciones', key: 'actions', sortable: false }] : []),
            ]}
            tableProps={{
              sx: {
                '& .MuiTableRow-root': {
                  height: 'auto',
                },
                '& .MuiTableCell-root': {
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontFamily: 'inherit',
                },
              },
            }}
          >
            {filteredSensors.map((sensor) => {
              const tipologia = getTipologiaFromProductType(sensor.product_type);
              return (
              <TableRow 
                key={sensor.id}
                onClick={() => canManage && openEditDialog(sensor)}
                sx={{
                  height: 'auto',
                  cursor: canManage ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: canManage ? 'action.hover' : 'transparent',
                  },
                  '& .MuiTableCell-root': {
                    py: 0.6,
                    fontSize: '0.75rem',
                    fontFamily: 'inherit',
                  },
                }}
              >
                <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                  {sensor.serial_number}
                </TableCell>
                <TableCell>
                  <Chip
                    label={tipologia}
                    size="small"
                    color={tipologia === 'Long-Gauge' ? 'primary' : 'secondary'}
                    sx={{ 
                      height: '22px', 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {sensor.product_type}
                </TableCell>
                <TableCell sx={{ maxWidth: '600px', minWidth: '400px' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      color: 'text.primary',
                      wordBreak: 'break-word',
                    }}
                  >
                    {sensor.product_description || '-'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 0.3,
                    }}
                  >
                    <span>{Number(sensor.strain_coefficient_a).toExponential(3)}</span>
                    <span style={{ 
                      color: theme.palette.text.secondary, 
                      fontSize: '0.75rem', 
                      fontFamily: 'monospace',
                      fontWeight: 'normal',
                    }}>
                      [με⁻¹]
                    </span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 0.3,
                    }}
                  >
                    <span>{Number(sensor.strain_coefficient_b).toExponential(3)}</span>
                    <span style={{ 
                      color: theme.palette.text.secondary, 
                      fontSize: '0.75rem', 
                      fontFamily: 'monospace',
                      fontWeight: 'normal',
                    }}>
                      [°C⁻¹]
                    </span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 0.3,
                    }}
                  >
                    <span>{sensor.free_fiber_length}</span>
                    <span style={{ 
                      color: theme.palette.text.secondary, 
                      fontSize: '0.75rem', 
                      fontFamily: 'monospace',
                      fontWeight: 'normal',
                    }}>
                      [m]
                    </span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 0.3,
                    }}
                  >
                    <span>{sensor.cte}</span>
                    <span style={{ 
                      color: theme.palette.text.secondary, 
                      fontSize: '0.75rem', 
                      fontFamily: 'monospace',
                      fontWeight: 'normal',
                    }}>
                      [με.°C⁻¹]
                    </span>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {sensor.temp_sensitivity_1 !== null ? (
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 0.3,
                      }}
                    >
                      <span>{Number(sensor.temp_sensitivity_1).toFixed(2)}</span>
                      <span style={{ 
                        color: theme.palette.text.secondary, 
                        fontSize: '0.75rem', 
                        fontFamily: 'monospace',
                        fontWeight: 'normal',
                      }}>
                        [°C]
                      </span>
                    </Box>
                  ) : (
                    <span style={{ color: theme.palette.text.disabled }}>-</span>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {sensor.temp_sensitivity_2 !== null ? (
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 0.3,
                      }}
                    >
                      <span>{Number(sensor.temp_sensitivity_2).toFixed(2)}</span>
                      <span style={{ 
                        color: theme.palette.text.secondary, 
                        fontSize: '0.75rem', 
                        fontFamily: 'monospace',
                        fontWeight: 'normal',
                      }}>
                        [°C]
                      </span>
                    </Box>
                  ) : (
                    <span style={{ color: theme.palette.text.disabled }}>-</span>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {sensor.temp_sensitivity_3 !== null ? (
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 0.3,
                      }}
                    >
                      <span>{Number(sensor.temp_sensitivity_3).toFixed(2)}</span>
                      <span style={{ 
                        color: theme.palette.text.secondary, 
                        fontSize: '0.75rem', 
                        fontFamily: 'monospace',
                        fontWeight: 'normal',
                      }}>
                        [°C]
                      </span>
                    </Box>
                  ) : (
                    <span style={{ color: theme.palette.text.disabled }}>-</span>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {sensor.temp_ref_wavelength !== null ? (
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 0.3,
                      }}
                    >
                      <span>{Number(sensor.temp_ref_wavelength).toFixed(2)}</span>
                      <span style={{ 
                        color: theme.palette.text.secondary, 
                        fontSize: '0.75rem', 
                        fontFamily: 'monospace',
                        fontWeight: 'normal',
                      }}>
                        [nm]
                      </span>
                    </Box>
                  ) : (
                    <span style={{ color: theme.palette.text.disabled }}>-</span>
                  )}
                </TableCell>
                {canManage && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <ActionButtonGroup
                      actions={[
                        {
                          icon: <Edit fontSize="small" />,
                          tooltip: t('common.edit', 'Editar'),
                          onClick: () => openEditDialog(sensor),
                        },
                        {
                          icon: <DeleteOutline fontSize="small" />,
                          tooltip: t('common.delete', 'Eliminar'),
                          onClick: () => requestDelete(sensor),
                          destructive: true,
                        },
                      ]}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
            })}
          </CorporateTable>
        )}

        {/* Diálogo de crear/editar */}
        <CorporateDialog
          open={isDialogOpen}
          onClose={closeDialogs}
          title={selectedSensor ? 'Editar Sensor FOS' : 'Nuevo Sensor FOS'}
          maxWidth="lg"
          fullWidth
        >
          <Box sx={{ pr: 1 }}>
            <Stack spacing={2}>
              {/* Información Básica */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>Información Básica</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <CorporateTextField
                      label="Serial Number *"
                      value={formState.serial_number}
                      onChange={(e) => handleInputChange('serial_number', e.target.value)}
                      error={!!formErrors.serial_number}
                      helperText={formErrors.serial_number}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CorporateTextField
                      label="Tipo de Producto *"
                      value={formState.product_type}
                      onChange={(e) => {
                        const newProductType = e.target.value;
                        const newTipologia = getTipologiaFromProductType(newProductType);
                        handleInputChange('product_type', newProductType);
                        handleInputChange('tipologia', newTipologia);
                      }}
                      error={!!formErrors.product_type}
                      helperText={formErrors.product_type}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth required size="small">
                      <InputLabel>Tipología *</InputLabel>
                      <Select
                        value={formState.tipologia}
                        label="Tipología *"
                        onChange={(e) => handleInputChange('tipologia', e.target.value as FOSTipologia)}
                        disabled
                      >
                        <MenuItem value="Long-Gauge">Long-Gauge</MenuItem>
                        <MenuItem value="Short-Gauge">Short-Gauge</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Tipología *</InputLabel>
                        <Select
                          value={formState.tipologia}
                          label="Tipología *"
                          onChange={(e) => handleInputChange('tipologia', e.target.value as FOSTipologia)}
                          disabled
                        >
                          <MenuItem value="Long-Gauge">Long-Gauge</MenuItem>
                          <MenuItem value="Short-Gauge">Short-Gauge</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  <Grid item xs={12}>
                    <CorporateTextField
                      label="Descripción del Producto"
                      value={formState.product_description}
                      onChange={(e) => handleInputChange('product_description', e.target.value)}
                      multiline
                      rows={2}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Especificaciones Técnicas */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>Especificaciones Técnicas</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="Gauge Length (GL)"
                      value={formState.gauge_length}
                      onChange={(e) => handleInputChange('gauge_length', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="Wavelength Range (WL)"
                      value={formState.wavelength_range}
                      onChange={(e) => handleInputChange('wavelength_range', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="Lead-in Length (LI)"
                      value={formState.lead_in_length}
                      onChange={(e) => handleInputChange('lead_in_length', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="Lead-out Length (LO)"
                      value={formState.lead_out_length}
                      onChange={(e) => handleInputChange('lead_out_length', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CorporateTextField
                      label="Tipo de Conector"
                      value={formState.connector_type}
                      onChange={(e) => handleInputChange('connector_type', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <CorporateTextField
                      label="Configuración de Cable"
                      value={formState.cable_config}
                      onChange={(e) => handleInputChange('cable_config', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <CorporateTextField
                      label="Configuración Adicional"
                      value={formState.additional_config}
                      onChange={(e) => handleInputChange('additional_config', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Parámetros STRAIN */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>Parámetros STRAIN</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="A [με⁻¹] *"
                      value={formState.strain_coefficient_a}
                      onChange={(e) => handleInputChange('strain_coefficient_a', e.target.value)}
                      error={!!formErrors.strain_coefficient_a}
                      helperText={formErrors.strain_coefficient_a || 'Coeficiente A'}
                      fullWidth
                      required
                      type="number"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="B [°C⁻¹] *"
                      value={formState.strain_coefficient_b}
                      onChange={(e) => handleInputChange('strain_coefficient_b', e.target.value)}
                      error={!!formErrors.strain_coefficient_b}
                      helperText={formErrors.strain_coefficient_b || 'Coeficiente B'}
                      fullWidth
                      required
                      type="number"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="L_FFL [m] *"
                      value={formState.free_fiber_length}
                      onChange={(e) => handleInputChange('free_fiber_length', e.target.value)}
                      error={!!formErrors.free_fiber_length}
                      helperText={formErrors.free_fiber_length || 'Longitud de fibra libre'}
                      fullWidth
                      required
                      type="number"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <CorporateTextField
                      label="CTE [με.°C⁻¹] *"
                      value={formState.cte}
                      onChange={(e) => handleInputChange('cte', e.target.value)}
                      error={!!formErrors.cte}
                      helperText={formErrors.cte || 'Coeficiente de expansión térmica'}
                      fullWidth
                      required
                      type="number"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Parámetros TEMPERATURE - Solo para Long-Gauge */}
              {formState.tipologia === 'Long-Gauge' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 600 }}>Parámetros TEMPERATURE (Opcional)</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={3}>
                      <CorporateTextField
                        label="TS1 [°C]"
                        value={formState.temp_sensitivity_1}
                        onChange={(e) => handleInputChange('temp_sensitivity_1', e.target.value)}
                        error={!!formErrors.temp_sensitivity_1}
                        helperText={formErrors.temp_sensitivity_1 || 'Sensibilidad de temperatura 1'}
                        fullWidth
                        type="number"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CorporateTextField
                        label="TS2 [°C]"
                        value={formState.temp_sensitivity_2}
                        onChange={(e) => handleInputChange('temp_sensitivity_2', e.target.value)}
                        error={!!formErrors.temp_sensitivity_2}
                        helperText={formErrors.temp_sensitivity_2 || 'Sensibilidad de temperatura 2'}
                        fullWidth
                        type="number"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CorporateTextField
                        label="TS3 [°C]"
                        value={formState.temp_sensitivity_3}
                        onChange={(e) => handleInputChange('temp_sensitivity_3', e.target.value)}
                        error={!!formErrors.temp_sensitivity_3}
                        helperText={formErrors.temp_sensitivity_3 || 'Sensibilidad de temperatura 3'}
                        fullWidth
                        type="number"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CorporateTextField
                        label="λ_T,ref [nm]"
                        value={formState.temp_ref_wavelength}
                        onChange={(e) => handleInputChange('temp_ref_wavelength', e.target.value)}
                        error={!!formErrors.temp_ref_wavelength}
                        helperText={formErrors.temp_ref_wavelength || 'Longitud de onda de referencia'}
                        fullWidth
                        type="number"
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <CorporateButton onClick={closeDialogs} disabled={isSubmitting}>
              Cancelar
            </CorporateButton>
            <CorporateButton
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : selectedSensor ? 'Actualizar' : 'Crear'}
            </CorporateButton>
          </Box>
        </CorporateDialog>

        {/* Diálogo de confirmación de eliminación */}
        <CorporateDialog
          open={isDeleteDialogOpen}
          onClose={closeDialogs}
          title="Eliminar Sensor FOS"
        >
          <Typography>
            ¿Estás seguro de que deseas eliminar el sensor{' '}
            <strong>{selectedSensor?.serial_number}</strong>? Esta acción no se puede deshacer.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <CorporateButton onClick={closeDialogs} disabled={isSubmitting}>
              Cancelar
            </CorporateButton>
            <CorporateButton
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Eliminando...' : 'Eliminar'}
            </CorporateButton>
          </Box>
        </CorporateDialog>

        {/* Modal informativo sobre fórmulas de cálculo */}
        <Dialog
          open={showFormulasInfo}
          onClose={() => setShowFormulasInfo(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600 }}>
            Fórmulas de Cálculo de Microstrains FOS
          </DialogTitle>
          <DialogContent>
            {/* Long-Gauge */}
            <Typography variant="h6" sx={{ mt: 1, mb: 1, fontWeight: 600, color: 'primary.main' }}>
              Sensores Long-Gauge (SC-01/T)
            </Typography>
            <Typography variant="body2" paragraph>
              Los sensores Long-Gauge utilizan una fórmula completa que incluye compensación de temperatura.
              Esta fórmula es adecuada para ensayos largos donde los efectos térmicos son significativos.
            </Typography>
            <Box component="code" sx={{ 
              display: 'block', 
              p: 1.5, 
              bgcolor: 'grey.100', 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              mb: 2,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              Δε = ((Δλ - B × ΔT) / (A × Δl)) + (ΔT × CTE_SS304 × Δl²) - (ΔT × CTE_substrate)
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
              <strong>Requisitos:</strong> Variables 'strain' y 'temp', λ₀, T₀, l_FAL, CTE_substrate, coeficientes A y B
            </Typography>

            {/* Short-Gauge */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600, color: 'secondary.main' }}>
              Sensores Short-Gauge (MS-03, SGCalsens)
            </Typography>
            <Typography variant="body2" paragraph>
              Los sensores Short-Gauge utilizan una fórmula simplificada sin compensación de temperatura.
              Son ideales para mediciones puntuales donde los efectos térmicos son despreciables.
            </Typography>
            <Box component="code" sx={{ 
              display: 'block', 
              p: 1.5, 
              bgcolor: 'grey.100', 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              mb: 2,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              ε = (λti - λt0) × (10³ / 1.2)
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              <strong>Requisitos:</strong> Solo variable 'strain' (wavelength), λ₀ (calculado automáticamente)
            </Typography>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Nota:</strong> El sistema detecta automáticamente el tipo de sensor y aplica la fórmula correspondiente.
                Los sensores SGCalsens y MS-03 no requieren parámetros de calibración (A, B, CTE, TS1-3, λ_T,ref).
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowFormulasInfo(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

